import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCounties } from "../api";
import { County } from "../types";

interface CountySelectorProps {
  value: string;
  onChange: (countyId: string) => void;
  /** Free/Scout county-lock (#864 follow-up): when provided, only these
   *  county ids are selectable — the rest of the fetched list is filtered
   *  out entirely rather than shown-but-rejected. `undefined` (Pro/team/
   *  admin) keeps the original fully-unrestricted behavior. Passing an
   *  array with 0-1 entries hides the control (see below) since there's
   *  nothing meaningful to switch between. */
  restrictTo?: string[];
}

function countyLabel(c: County): string {
  return `${c.name}, ${c.state}`;
}

// Searchable single-select county picker (replaces the flat <select> ahead
// of the 102-county activation — #80). Deliberately mirrors
// SearchAutocomplete.tsx's combobox pattern (search-input-wrap /
// autocomplete-list / autocomplete-item / autocomplete-empty classes, same
// open-on-focus + arrow-key + Enter/Escape + outside-click-to-close model)
// rather than CountyPickerModal.tsx's pattern: that modal is a full-screen
// multi-select onboarding step with no type-to-filter today, so it isn't a
// fit for a single-select top-bar control that must fire onChange
// immediately. Reusing the autocomplete classes keeps the two search-style
// widgets in the app visually and behaviorally consistent ("one product")
// without inventing new tokens.
const CountySelector: React.FC<CountySelectorProps> = ({ value, onChange, restrictTo }) => {
  const [counties, setCounties] = useState<County[]>([]);
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getCounties()
      .then(setCounties)
      .catch(() => setCounties([]));
  }, []);

  // Close on outside click — same pattern as SearchAutocomplete.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Free/Scout county-lock: filter the fetched list down to the allowed
  // set before anything else touches it (search, keyboard nav, selected
  // lookup) — restrictTo === undefined (Pro/team/admin) is a no-op.
  const availableCounties = useMemo(
    () => (restrictTo ? counties.filter((c) => restrictTo.includes(c.id)) : counties),
    [counties, restrictTo]
  );

  const selected = availableCounties.find((c) => c.id === value) || null;

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return availableCounties;
    return availableCounties.filter((c) => countyLabel(c).toLowerCase().includes(q));
  }, [availableCounties, filterText]);

  const openList = () => {
    setFilterText("");
    const idx = selected ? availableCounties.findIndex((c) => c.id === selected.id) : 0;
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  };

  const closeList = () => {
    setOpen(false);
    setFilterText("");
  };

  const commitSelection = (c: County) => {
    onChange(c.id);
    closeList();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
    setActiveIndex(0);
    if (!open) setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        openList();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length) setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length) setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = filtered[activeIndex];
      if (option) commitSelection(option);
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeList();
    } else if (e.key === "Tab") {
      closeList();
    }
  };

  // Hide ONLY while the list is still empty (loading / network error).
  // When exactly one active county exists globally, we still render the
  // picker so the chrome doesn't shift on county activation. Pre-#multi-
  // county this hid itself for ≤1 — that masked the new-county control
  // appearing on next deploy and felt like a layout regression.
  //
  // restrictTo is a different case: a Free/Scout user with 0-1 chosen
  // counties has nothing to switch *to*, so there's no reason to show a
  // combobox at all (vs. showing one whose only option is a no-op click).
  // Scout with 2-3 chosen counties still gets the restricted list below.
  if (availableCounties.length === 0 || (restrictTo && availableCounties.length <= 1)) {
    return null;
  }

  const displayValue = open ? filterText : selected ? countyLabel(selected) : "";
  const activeOption = open ? filtered[activeIndex] : undefined;

  return (
    <div className="county-selector" data-tour="county-selector" ref={containerRef}>
      <div className="search-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="county-selector-listbox"
          aria-activedescendant={activeOption ? `county-option-${activeOption.id}` : undefined}
          aria-autocomplete="list"
          aria-label="Select county"
          title="Select county"
          placeholder="Search counties…"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (!open) openList();
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>

      {open && (
        <ul id="county-selector-listbox" className="autocomplete-list" role="listbox" aria-label="Counties">
          {filtered.length === 0 ? (
            <li className="autocomplete-empty">No counties match &ldquo;{filterText}&rdquo;</li>
          ) : (
            filtered.map((c, i) => {
              const isSelected = c.id === value;
              return (
                <li
                  key={c.id}
                  id={`county-option-${c.id}`}
                  role="option"
                  aria-selected={isSelected}
                  className={
                    "autocomplete-item" +
                    (i === activeIndex ? " autocomplete-item--active" : "") +
                    (isSelected ? " county-option--selected" : "")
                  }
                  onMouseDown={() => commitSelection(c)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  {countyLabel(c)}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default CountySelector;
